import { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const LiveKitContext = createContext(null);

export function useLiveKit() {
  const ctx = useContext(LiveKitContext);
  if (!ctx) throw new Error("useLiveKit must be used within LiveKitProvider");
  return ctx;
}

export function LiveKitProvider({ children }) {
  const { user, token } = useAuth();

  // Connection state
  const [connectionState, setConnectionState] = useState("disconnected"); // disconnected | connecting | connected
  const [livekitMode, setLivekitMode] = useState("unknown"); // unknown | simulation | live
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [wsUrl, setWsUrl] = useState("");
  const lkRoomRef = useRef(null);
  const [lkParticipants, setLkParticipants] = useState([]);

  // Media state
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [screenShare, setScreenShare] = useState(false);

  // Timer
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  // Start/stop timer based on connection state
  useEffect(() => {
    if (connectionState === "connected") {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [connectionState]);

  const formatTime = (s) =>
    `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  // Update participant list from room
  const updateParticipants = useCallback((room) => {
    if (!room) return;
    const all = [room.localParticipant, ...Array.from(room.remoteParticipants.values())];
    setLkParticipants(all);
  }, []);

  // Join a room
  const joinRoom = useCallback(async (roomId) => {
    if (connectionState === "connected" && activeRoomId === roomId) return;

    // If already in another room, leave first
    if (lkRoomRef.current) {
      lkRoomRef.current.disconnect();
      lkRoomRef.current = null;
    }

    setConnectionState("connecting");
    setActiveRoomId(roomId);

    const headers = { Authorization: `Bearer ${token}` };

    try {
      // 1. Check config
      const configRes = await axios.get(`${API}/api/livekit/config`);
      if (configRes.data.mode !== "live") {
        setLivekitMode("simulation");
        setConnectionState("connected");
        return;
      }

      // 2. Get token
      const tokenRes = await axios.post(`${API}/api/livekit/token`, {
        room_name: roomId,
        role: "organizer",
      }, { headers });

      if (tokenRes.data.mode !== "live" || !tokenRes.data.token) {
        setLivekitMode("simulation");
        setConnectionState("connected");
        return;
      }

      setWsUrl(tokenRes.data.ws_url);
      setLivekitMode("live");

      // 3. Connect via livekit-client
      const { Room, RoomEvent } = await import("livekit-client");
      const room = new Room();

      room.on(RoomEvent.TrackSubscribed, () => updateParticipants(room));
      room.on(RoomEvent.TrackUnsubscribed, () => updateParticipants(room));
      room.on(RoomEvent.ParticipantConnected, () => updateParticipants(room));
      room.on(RoomEvent.ParticipantDisconnected, () => updateParticipants(room));
      room.on(RoomEvent.LocalTrackPublished, () => updateParticipants(room));
      room.on(RoomEvent.Disconnected, (reason) => {
        // If the room was voluntarily disconnected (via leaveRoom), don't fallback
        if (lkRoomRef.current === null) return;
        // Involuntary disconnect: fallback to simulation mode (keep conference "alive")
        toast.error("Connexion LiveKit perdue — mode simulation active");
        lkRoomRef.current = null;
        setLivekitMode("simulation");
        // Keep connectionState as "connected" so floating bar stays visible
      });

      await room.connect(tokenRes.data.ws_url, tokenRes.data.token);
      lkRoomRef.current = room;
      setConnectionState("connected");

      // Enable camera and mic (graceful)
      try {
        await room.localParticipant.enableCameraAndMicrophone();
      } catch (mediaErr) {
        console.warn("Camera/mic unavailable:", mediaErr.message);
        toast.info("Camera/micro non disponibles — connecte en mode ecoute");
      }
      updateParticipants(room);
      toast.success("Connecte a la salle LiveKit");

    } catch (e) {
      console.error("LiveKit join error:", e);
      setLivekitMode("simulation");
      setConnectionState("connected");
      toast.error("Erreur connexion LiveKit — mode simulation");
    }
  }, [token, connectionState, activeRoomId, updateParticipants]);

  // Leave room (voluntary disconnect)
  const leaveRoom = useCallback(() => {
    if (lkRoomRef.current) {
      lkRoomRef.current.disconnect();
      lkRoomRef.current = null;
    }
    setConnectionState("disconnected");
    setActiveRoomId(null);
    setLkParticipants([]);
    setLivekitMode("unknown");
    setMicOn(true);
    setCamOn(true);
    setScreenShare(false);
    setElapsed(0);
  }, []);

  // Media toggles
  const toggleMic = useCallback(async () => {
    if (lkRoomRef.current && livekitMode === "live") {
      try {
        await lkRoomRef.current.localParticipant.setMicrophoneEnabled(!micOn);
      } catch (e) { console.error(e); }
    }
    setMicOn(v => !v);
  }, [micOn, livekitMode]);

  const toggleCam = useCallback(async () => {
    if (lkRoomRef.current && livekitMode === "live") {
      try {
        await lkRoomRef.current.localParticipant.setCameraEnabled(!camOn);
      } catch (e) { console.error(e); }
    }
    setCamOn(v => !v);
  }, [camOn, livekitMode]);

  const toggleScreenShare = useCallback(async () => {
    if (lkRoomRef.current && livekitMode === "live") {
      try {
        await lkRoomRef.current.localParticipant.setScreenShareEnabled(!screenShare);
      } catch (e) { console.error(e); }
    }
    setScreenShare(v => !v);
  }, [screenShare, livekitMode]);

  const value = {
    // State
    connectionState,
    livekitMode,
    activeRoomId,
    wsUrl,
    lkRoom: lkRoomRef.current,
    lkParticipants,
    micOn,
    camOn,
    screenShare,
    elapsed,

    // Functions
    joinRoom,
    leaveRoom,
    toggleMic,
    toggleCam,
    toggleScreenShare,
    formatTime,
    updateParticipants,
  };

  return (
    <LiveKitContext.Provider value={value}>
      {children}
    </LiveKitContext.Provider>
  );
}
